import {useMutation, useQuery} from '@apollo/client';
import TableCell from '@material-ui/core/TableCell/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow/TableRow';
import Skeleton from '@material-ui/lab/Skeleton';
import {Club, User} from 'packages/common-types/schema-types';
import {PLAYERS_HEADERS} from 'packages/constants/table-constants';
import ConfirmAction from 'packages/dialogs/ConfirmAction';
import {DELETE_USER} from 'packages/graphql/v2/mutations';
import {
  GET_USERS,
  GET_TOURNAMENTS,
  GET_CLUBS,
} from 'packages/graphql/v2/queries';
import {useModal} from 'packages/hooks/use-modal';
import useTablePagination from 'packages/hooks/use-table-pagination';
import useTableSearch from 'packages/hooks/use-table-search';
import BaseTable from 'packages/tables/BaseTable';
import TableMenu from 'packages/common-ui/TableMenu';
import {useState, useRef, useCallback, useEffect} from 'react';
import Select from 'react-select';
import Swal from 'sweetalert2';
import MenuItem from '@material-ui/core/MenuItem/MenuItem';
import AddToTournament from 'packages/dialogs/AddToTournament';
import DeleteToTournamet from 'packages/dialogs/DeleteToTournament';
import AddToClub from 'packages/dialogs/AddToClub';
import useStyles from '../../tables/tables-styles';

type GetUsersData = {
  getUsers: User[];
};

type DeleteAdminVars = {
  uid: string;
};

type GetClubsData = {
  getClubs: Club[];
};

const Players = () => {
  const {data, loading: fetching} = useQuery<GetUsersData>(GET_USERS);
  const {data: registeredClubs} = useQuery<GetClubsData>(GET_CLUBS);
  console.log('REGISTERED CLUBS => ', registeredClubs);
  const [selectedUser, setSelectedUser] = useState('');
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const {toggle, modalOpen} = useModal();
  const {toggle: toggleDelete, modalOpen: modalOpenDelete} = useModal();
  const {toggle: toggleConfirm, modalOpen: openConfirm} = useModal();
  const {toggle: toggleClub, modalOpen: modalOpenClub} = useModal();
  const {
    onChangePage: onChangePagination,
    onChangeRowsPerPage,
    page,
    rowsPerPage,
  } = useTablePagination();

  useEffect(() => {
    if (registeredClubs) {
      setClubs(registeredClubs?.getClubs);
    }
  }, [data, registeredClubs]);

  const classes = useStyles(); // this variable is to put margin-top for the select bar.
  // Todo: Add Optimistic responses.
  const [deleteUser] = useMutation<boolean, DeleteAdminVars>(DELETE_USER, {
    refetchQueries: [{query: GET_USERS}, {query: GET_TOURNAMENTS}],
  });
  const {filteredRows, onChange} = useTableSearch(data?.getUsers);
  const selectRef = useRef(null);

  const handleDeleteAccount = async () => {
    try {
      setLoading(true);
      await deleteUser({variables: {uid: selectedUser}});
      Swal.fire({
        title: 'Hecho!',
        text: 'El usuario ha sido eliminado',
        icon: 'success',
      });
      selectRef.current.select.clearValue();
    } catch (error) {
      Swal.fire({
        title: 'Error',
        text: 'Ocurrió un error al eliminar el usuario',
        icon: 'error',
      });
    } finally {
      setLoading(false);
      toggleConfirm();
    }
  };

  /**
   * @returns this function returns the table with the information that users filter.
   */
  const renderTableData = useCallback(() => {
    if (clubs) {
      return filteredRows
        ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map((user) => {
          const {uid, fullName, address, gender, clubId, status} = user as User;
          const clubName = clubs.find(({_id}) => _id === clubId)?.name;
          return (
            <TableRow key={uid}>
              <TableCell component="th" scope="row">
                {fullName}
              </TableCell>
              <TableCell align="right">{address.city}</TableCell>
              <TableCell align="right">{gender}</TableCell>
              <TableCell align="right">{status}</TableCell>
              <TableCell align="right">{clubName || `Sin club`}</TableCell>
              <TableCell align="right" style={{width: 250}}>
                <TableMenu>
                  <MenuItem
                    onClick={() => {
                      setSelectedUser(uid);
                      toggle();
                    }}
                  >
                    Añadir a un torneo
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setSelectedUser(uid);
                      toggleClub();
                    }}
                  >
                    Añadir a un club
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setSelectedUser(uid);
                      toggleDelete();
                    }}
                  >
                    Remover de un torneo
                  </MenuItem>

                  <MenuItem
                    disabled={status === 'Inscrito'}
                    onClick={() => {
                      setSelectedUser(uid);
                      toggleConfirm();
                    }}
                  >
                    Eliminar cuenta del jugador
                  </MenuItem>
                </TableMenu>
              </TableCell>
            </TableRow>
          );
        });
    }
    return [];
  }, [
    filteredRows,
    page,
    rowsPerPage,
    toggle,
    toggleClub,
    toggleDelete,
    toggleConfirm,
    clubs,
  ]);

  if (!data || fetching) {
    return (
      <>
        <div style={{marginTop: 30}}>
          <Skeleton variant="rect" height={38} />
          <Skeleton variant="rect" height={38} />
          <Skeleton variant="rect" height={38} />
          <Skeleton variant="rect" height={38} />
          <Skeleton variant="rect" height={38} />
        </div>
      </>
    );
  }

  return (
    <>
      <ConfirmAction
        title="Eliminar cuenta"
        loading={loading}
        description="Una vez elimines la cuenta, perderas toda la información relacionada a este usuario"
        onConfirm={handleDeleteAccount}
        modalOpen={openConfirm}
        toggle={toggleConfirm}
      />
      <AddToClub
        uid={selectedUser}
        modalOpen={modalOpenClub}
        toggle={toggleClub}
      />
      <AddToTournament
        uidUser={selectedUser}
        modalOpen={modalOpen}
        toggle={toggle}
      />
      <DeleteToTournamet
        uidUser={selectedUser}
        modalOpen={modalOpenDelete}
        toggle={toggleDelete}
      />
      <Select
        ref={selectRef}
        className={classes.table_container}
        options={filteredRows}
        isClearable
        isLoading={fetching}
        getOptionLabel={({fullName}) => fullName}
        getOptionValue={({uid}) => uid}
        placeholder="Buscar una cuenta"
        onChange={onChange}
      />
      <BaseTable headers={PLAYERS_HEADERS}>{renderTableData()}</BaseTable>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={data?.getUsers.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onChangePagination}
        onRowsPerPageChange={onChangeRowsPerPage}
      />
    </>
  );
};

export default Players;
